import { selectUnit } from '@formatjs/intl-utils';
import { DateTimeStr, formatDuration, Seconds } from '@mi-front/core';
import { Box } from '@mui/material';
import React from 'react';
import {
  FormattedDate,
  FormattedRelativeTime,
  FormattedTime,
  IntlProvider,
  useIntl,
} from 'react-intl';
import FormattedDuration from 'react-intl-formatted-duration';

export enum DateTimeFormat {
  DAY_MONTH = 'DD/MM',
  DATE = 'DD/MM/YYYY',
  TIME = 'HH:mm:ss',
  LONG_DATE = "dddd Do MMMM 'YY",
  LONG_DATE_TIME = "dddd Do MMMM 'YY - HH:mm:ss",
}

export function LocaleDate(
  props: React.ComponentProps<typeof FormattedDate> & {
    value: DateTimeStr | string;
    className?: string;
  },
) {
  const { value, className, ...rest } = props;

  return (
    <span className={className}>
      <FormattedDate value={new Date(props.value)} {...rest} />
    </span>
  );
}

export function LocaleTime(
  props: React.ComponentProps<typeof FormattedTime> & {
    value: DateTimeStr | string;
  },
) {
  return <FormattedTime hour="2-digit" minute="2-digit" {...props} value={new Date(props.value)} />;
}

const ONE_MINUTE_MS = 60 * 1000;

function isLessThanOneMinuteAgo(date: Date): boolean {
  return Date.now() - date.getTime() < ONE_MINUTE_MS;
}

export function LocaleRelativeTime(
  props: Omit<React.ComponentProps<typeof FormattedRelativeTime>, 'value'> & {
    value: number | string;
  },
) {
  const { value: _value, ...rest } = props;
  const { value, unit } = selectUnit(new Date(_value));
  const { $t } = useIntl();

  if (isLessThanOneMinuteAgo(new Date(_value))) {
    return (
      <Box display="inline-flex" flexDirection="column">
        {$t({
          id: 'date.justNow',
          defaultMessage: 'Hace instantes',
        })}
      </Box>
    );
  }

  return <FormattedRelativeTime {...rest} value={value} unit={unit} />;
}

interface DurationProps {
  readonly value: number;
  readonly verbose?: boolean;
}

export const DurationOld = ({ value, verbose }: DurationProps) => (
  <span>{formatDuration(value, { verbose })}</span>
);

export function Duration(p: { seconds: Seconds }) {
  return <FormattedDuration seconds={p.seconds} unitDisplay="short" />;
}

export const DurationForRange = ({
  from,
  to,
  unitDisplay = 'short',
}: {
  from: DateTimeStr | string;
  to: DateTimeStr | string;
  verbose?: boolean;
  unitDisplay?: 'narrow' | 'short' | 'long' | 'full';
}) => {
  const seconds = (Number(new Date(to)) - Number(new Date(from))) / 1000;

  if (unitDisplay === 'narrow') {
    return (
      <IntlProvider locale="en-US">
        <FormattedDuration seconds={seconds} unitDisplay={unitDisplay} />
      </IntlProvider>
    );
  }

  return <FormattedDuration seconds={seconds} unitDisplay={unitDisplay} />;
};

// Type for formatMessage from react-intl
export type TranslateRawFn = (descriptor: {
  id: string;
  defaultMessage?: string;
  values?: Record<string, any>;
}) => string;
